import { useState, useMemo, useEffect } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { SuperAdminLayout } from "@/components/superadmin/SuperAdminLayout"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useToast } from "@/hooks/use-toast"
import {
  Search, Plus, Pencil, Trash2, Clock, BarChart3,
  CheckCircle2, XCircle, DollarSign, User, Briefcase, Timer,
  Download, FileText, MoreHorizontal
} from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { DataTable, EmptyState, Page, PageHeader, Pagination, Panel, SearchInput, Segmented, StatCard, StatGrid, Tag, Toolbar, rowClass, td, th, usePagination, type Tone } from "@/components/superadmin/kit"
import { cn } from "@/lib/utils"
import type { HoraTecnica, TipoHora, Projeto } from "@/types/comercial"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"

// ── Helpers ──────────────────────────────────────────────────────────────────
function uid() { return crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) }
function fmtDate(d: string) { return d ? new Date(d + "T00:00:00").toLocaleDateString("pt-BR") : "—" }
function fmtHoras(h: number) { return `${h}h` }

function fmtCurrency(v: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v)
}

// ── Types & Constants ────────────────────────────────────────────────────────
const PROFISSIONAIS_DEFAULT = ["André Costa", "Lucas Ferreira", "Mariana Souza", "Rafael Alves"]

function emptyHora(): HoraTecnica {
  return {
    id: uid(),
    projetoId: "",
    projetoNome: "",
    profissional: "",
    data: new Date().toISOString().split("T")[0],
    horasRegistradas: 1,
    tipo: "cobravel",
    descricao: "",
    tarefa: "",
    aprovado: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

function emptyProjeto(): Projeto {
  return {
    id: uid(),
    nome: "",
    clienteNome: "",
    clienteEmail: "",
    status: "ativo",
    horasPrevistas: 0,
    valorHora: 130,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
const HorasTecnicas = () => {
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState("dashboard")

  // Stopwatch/timer state
  const [timerActive, setTimerActive] = useState(false)
  const [timerIntervalId, setTimerIntervalId] = useState<any>(null)
  const [secondsElapsed, setSecondsElapsed] = useState(0)

  useEffect(() => {
    return () => {
      if (timerIntervalId) clearInterval(timerIntervalId)
    }
  }, [timerIntervalId])

  const handleStartTimer = () => {
    if (timerActive) return
    setTimerActive(true)
    const id = setInterval(() => {
      setSecondsElapsed(prev => prev + 1)
    }, 1000)
    setTimerIntervalId(id)
  }

  const handlePauseTimer = () => {
    if (!timerActive) return
    setTimerActive(false)
    if (timerIntervalId) {
      clearInterval(timerIntervalId)
      setTimerIntervalId(null)
    }
  }

  const handleStopTimer = () => {
    setTimerActive(false)
    if (timerIntervalId) {
      clearInterval(timerIntervalId)
      setTimerIntervalId(null)
    }
    const decimalHours = Math.round((secondsElapsed / 3600) * 100) / 100
    const finalHours = Math.max(0.01, decimalHours)
    setFormHora(prev => ({ ...prev, horasRegistradas: finalHours }))
    setSecondsElapsed(0)
    toast({ title: "Cronômetro parado", description: `Tempo de ${formatStopwatchTime(secondsElapsed)} convertido para ${finalHours}h` })
  }

  const formatStopwatchTime = (totalSeconds: number) => {
    const hrs = Math.floor(totalSeconds / 3600)
    const mins = Math.floor((totalSeconds % 3600) / 60)
    const secs = totalSeconds % 60
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  // Carregar do localStorage ou iniciar com dados ricos
  const [horas, setHoras] = useState<HoraTecnica[]>(() => {
    const saved = localStorage.getItem("itacondo_horas")
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch (e) {
        console.error("Erro parsing horas:", e)
      }
    }
    return [
      {
        id: "h1",
        projetoId: "p1",
        projetoNome: "Instalação de Câmeras — Condomínio Vista Alegre",
        profissional: "Lucas Ferreira",
        data: "2026-06-15",
        horasRegistradas: 8,
        tipo: "cobravel",
        descricao: "Instalação física dos suportes e fixação de 6 Câmeras IP Intelbras nos blocos A e B.",
        tarefa: "Fixação de suportes e cabeamento externo",
        aprovado: true,
        createdAt: "2026-06-15T18:00:00Z",
        updatedAt: "2026-06-15T18:00:00Z"
      },
      {
        id: "h2",
        projetoId: "p1",
        projetoNome: "Instalação de Câmeras — Condomínio Vista Alegre",
        profissional: "Lucas Ferreira",
        data: "2026-06-16",
        horasRegistradas: 6,
        tipo: "cobravel",
        descricao: "Fusão de fibra óptica e passagem de cabo UTP blindado da portaria até o bloco C.",
        tarefa: "Passagem de cabo estruturado",
        aprovado: true,
        createdAt: "2026-06-16T17:00:00Z",
        updatedAt: "2026-06-16T17:00:00Z"
      },
      {
        id: "h3",
        projetoId: "p2",
        projetoNome: "Setup de Servidor e IA de Vídeo — Condomínio Central Park",
        profissional: "Mariana Souza",
        data: "2026-06-18",
        horasRegistradas: 5,
        tipo: "cobravel",
        descricao: "Configuração do servidor de inteligência artificial de vídeo e calibração de detecção facial.",
        tarefa: "Setup de IA de face-api",
        aprovado: true,
        createdAt: "2026-06-18T16:00:00Z",
        updatedAt: "2026-06-18T16:00:00Z"
      },
      {
        id: "h4",
        projetoId: "p1",
        projetoNome: "Instalação de Câmeras — Condomínio Vista Alegre",
        profissional: "André Costa",
        data: "2026-06-20",
        horasRegistradas: 4,
        tipo: "cobravel",
        descricao: "Mapeamento dos pontos de câmeras e análise técnica da planta baixa com o síndico.",
        tarefa: "Levantamento técnico inicial",
        aprovado: true,
        createdAt: "2026-06-20T12:00:00Z",
        updatedAt: "2026-06-20T12:00:00Z"
      },
      {
        id: "h5",
        projetoId: "p2",
        projetoNome: "Setup de Servidor e IA de Vídeo — Condomínio Central Park",
        profissional: "André Costa",
        data: "2026-06-22",
        horasRegistradas: 3,
        tipo: "cobravel",
        descricao: "Treinamento da equipe de portaria sobre liveness detection e emissão de relatórios de horas.",
        tarefa: "Treinamento operacional",
        aprovado: false,
        createdAt: "2026-06-22T15:00:00Z",
        updatedAt: "2026-06-22T15:00:00Z"
      },
      {
        id: "h6",
        projetoId: "p3",
        projetoNome: "Cabeamento de Fibra — Condomínio Parque das Flores",
        profissional: "Rafael Alves",
        data: "2026-06-24",
        horasRegistradas: 2,
        tipo: "nao_cobravel",
        descricao: "Visita de garantia para ajuste de ângulo da câmera do portão principal.",
        tarefa: "Suporte de garantia técnica",
        aprovado: true,
        createdAt: "2026-06-24T11:00:00Z",
        updatedAt: "2026-06-24T11:00:00Z"
      }
    ]
  })

  const [projetos, setProjetos] = useState<Projeto[]>(() => {
    const saved = localStorage.getItem("itacondo_projetos")
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch (e) {
        console.error("Erro parsing projetos:", e)
      }
    }
    return [
      {
        id: "p1",
        nome: "Instalação de Câmeras — Condomínio Vista Alegre",
        clienteNome: "Carlos Eduardo da Silva",
        clienteEmail: "sindico.vistaalegre@gmail.com",
        status: "ativo",
        horasPrevistas: 40,
        createdAt: "2026-06-01T10:00:00Z",
        updatedAt: "2026-06-01T10:00:00Z"
      },
      {
        id: "p2",
        nome: "Setup de Servidor e IA de Vídeo — Condomínio Central Park",
        clienteNome: "Maria Oliveira",
        clienteEmail: "centralpark.adm@gmail.com",
        status: "ativo",
        horasPrevistas: 20,
        createdAt: "2026-06-02T11:00:00Z",
        updatedAt: "2026-06-02T11:00:00Z"
      },
      {
        id: "p3",
        nome: "Cabeamento de Fibra — Condomínio Parque das Flores",
        clienteNome: "Roberto Souza",
        clienteEmail: "flores.condominio@hotmail.com",
        status: "concluido",
        horasPrevistas: 30,
        createdAt: "2026-06-03T12:00:00Z",
        updatedAt: "2026-06-03T12:00:00Z"
      }
    ]
  })

  // Efeitos para sincronizar localStorage
  useEffect(() => { localStorage.setItem("itacondo_horas", JSON.stringify(horas)) }, [horas])
  useEffect(() => { localStorage.setItem("itacondo_projetos", JSON.stringify(projetos)) }, [projetos])

  // Filtros Geral e Lançamentos
  const [search, setSearch] = useState("")
  const [filterProjeto, setFilterProjeto] = useState("all")
  const [filterTipo, setFilterTipo] = useState<string>("all")
  const [filterMes, setFilterMes] = useState(new Date().toISOString().slice(0, 7))

  // Filtros específicos do Dashboard
  const [dashboardPeriodo, setDashboardPeriodo] = useState("mes")
  const [customStartDate, setCustomStartDate] = useState("")
  const [customEndDate, setCustomEndDate] = useState("")
  const [dashboardProjetoId, setDashboardProjetoId] = useState("all")

  const [horaOpen, setHoraOpen] = useState(searchParams.get("action") === "new")
  const [editingHora, setEditingHora] = useState<HoraTecnica | null>(null)
  const [formHora, setFormHora] = useState<HoraTecnica>(emptyHora())

  const [projetoOpen, setProjetoOpen] = useState(false)
  const [editingProjeto, setEditingProjeto] = useState<Projeto | null>(null)
  const [formProjeto, setFormProjeto] = useState<Projeto>(emptyProjeto())

  // Filtrar horas da listagem geral
  const horasFiltradas = useMemo(() => {
    return horas.filter(h => {
      const q = search.toLowerCase()
      const matchSearch = h.projetoNome.toLowerCase().includes(q) || h.profissional.toLowerCase().includes(q) || h.descricao.toLowerCase().includes(q)
      const matchProjeto = filterProjeto === "all" || h.projetoId === filterProjeto
      const matchTipo = filterTipo === "all" || h.tipo === filterTipo
      const matchMes = !filterMes || h.data.startsWith(filterMes)
      return matchSearch && matchProjeto && matchTipo && matchMes
    })
  }, [horas, search, filterProjeto, filterTipo, filterMes])

  const horasPager = usePagination(horasFiltradas, 15)
  useEffect(() => horasPager.reset(), [search, filterProjeto, filterTipo, filterMes]) // eslint-disable-line react-hooks/exhaustive-deps
  const [deleting, setDeleting] = useState<{ kind: "hora" | "projeto"; id: string } | null>(null)

  // KPIs da listagem geral filtrada
  const stats = useMemo(() => {
    const cobravel = horasFiltradas.filter(h => h.tipo === "cobravel").reduce((s, h) => s + h.horasRegistradas, 0)
    const naoCobravel = horasFiltradas.filter(h => h.tipo === "nao_cobravel").reduce((s, h) => s + h.horasRegistradas, 0)
    return { total: cobravel + naoCobravel, cobravel, naoCobravel, registros: horasFiltradas.length }
  }, [horasFiltradas])

  // Relatório consolidado por projeto
  const relatorioProjeto = useMemo(() => {
    const map: Record<string, { nome: string; cobravel: number; naoCobravel: number; total: number }> = {}
    horasFiltradas.forEach(h => {
      if (!map[h.projetoId]) map[h.projetoId] = { nome: h.projetoNome, cobravel: 0, naoCobravel: 0, total: 0 }
      map[h.projetoId].total += h.horasRegistradas
      if (h.tipo === "cobravel") map[h.projetoId].cobravel += h.horasRegistradas
      else map[h.projetoId].naoCobravel += h.horasRegistradas
    })
    return Object.values(map)
  }, [horasFiltradas])

  // ── DASHBOARD LOGIC ──
  const dashboardHorasFiltradas = useMemo(() => {
    return horas.filter(h => {
      if (dashboardProjetoId !== "all" && h.projetoId !== dashboardProjetoId) return false
      const dateVal = new Date(h.data + "T00:00:00")
      const now = new Date()
      if (dashboardPeriodo === "semana") {
        const d = new Date()
        d.setDate(now.getDate() - 7)
        d.setHours(0, 0, 0, 0)
        return dateVal >= d
      }
      if (dashboardPeriodo === "mes") {
        const start = new Date(now.getFullYear(), now.getMonth(), 1)
        return dateVal >= start
      }
      if (dashboardPeriodo === "mes_anterior") {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)
        return dateVal >= start && dateVal <= end
      }
      if (dashboardPeriodo === "personalizado") {
        if (customStartDate && dateVal < new Date(customStartDate + "T00:00:00")) return false
        if (customEndDate && dateVal > new Date(customEndDate + "T23:59:59")) return false
      }
      return true
    })
  }, [horas, dashboardPeriodo, dashboardProjetoId, customStartDate, customEndDate])

  const dashboardStats = useMemo(() => {
    const cobravel = dashboardHorasFiltradas.filter(h => h.tipo === "cobravel").reduce((s, h) => s + h.horasRegistradas, 0)
    return {
      total: dashboardHorasFiltradas.reduce((s, h) => s + h.horasRegistradas, 0),
      cobravel,
      faturavelValue: dashboardHorasFiltradas.filter(h => h.tipo === "cobravel").reduce((sum, h) => {
        const proj = projetos.find(p => p.id === h.projetoId)
        const rate = proj?.valorHora ?? 130
        return sum + h.horasRegistradas * rate
      }, 0),
      concluidas: dashboardHorasFiltradas.filter(h => h.aprovado).length,
      clientesAtendidos: new Set(dashboardHorasFiltradas.map(h => h.projetoId)).size
    }
  }, [dashboardHorasFiltradas, projetos])

  const chartData = useMemo(() => {
    const map: Record<string, any> = {}
    dashboardHorasFiltradas.forEach(h => {
      const day = h.data.split("-").slice(1).reverse().join("/")
      if (!map[day]) map[day] = { data: day, cobravel: 0, naoCobravel: 0 }
      if (h.tipo === "cobravel") map[day].cobravel += h.horasRegistradas
      else map[day].naoCobravel += h.horasRegistradas
    })
    return Object.values(map).sort((a: any, b: any) => {
      const [da, ma] = a.data.split("/").map(Number)
      const [db, mb] = b.data.split("/").map(Number)
      return ma === mb ? da - db : ma - mb
    })
  }, [dashboardHorasFiltradas])

  const dashboardProjetosResumo = useMemo(() => {
    const map: Record<string, any> = {}
    dashboardHorasFiltradas.forEach(h => {
      const proj = projetos.find(p => p.id === h.projetoId)
      if (!map[h.projetoId]) {
        map[h.projetoId] = {
          id: h.projetoId,
          nome: h.projetoNome,
          cliente: proj?.clienteNome ?? "Cliente Especial",
          total: 0,
          cobravel: 0,
          faturavel: 0
        }
      }
      map[h.projetoId].total += h.horasRegistradas
      if (h.tipo === "cobravel") {
        map[h.projetoId].cobravel += h.horasRegistradas
        const rate = proj?.valorHora ?? 130
        map[h.projetoId].faturavel += h.horasRegistradas * rate
      }
    })
    return Object.values(map).sort((a: any, b: any) => b.total - a.total)
  }, [dashboardHorasFiltradas, projetos])

  // ── PDF GENERATOR ──
  const handleDownloadPDF = async () => {
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()

    // 1. Carregar Logotipo ItaCondo
    try {
      const getLogoBase64 = () => new Promise<string>((resolve) => {
        const img = new Image()
        img.src = '/logo_sfundo.png'
        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')
          if (ctx) {
            ctx.drawImage(img, 0, 0)
            resolve(canvas.toDataURL('image/png'))
          } else resolve('')
        }
        img.onerror = () => resolve('')
      })
      const logoBase64 = await getLogoBase64()
      if (logoBase64) {
        doc.addImage(logoBase64, 'PNG', 15, 8, 38, 12)
      } else {
        // Fallback Vetorial
        doc.setFillColor(14, 165, 233)
        doc.rect(15, 12, 4, 12, 'F')
        doc.setFillColor(3, 7, 18)
        doc.rect(20, 8, 4, 16, 'F')
        doc.setFillColor(16, 185, 129)
        doc.rect(25, 14, 4, 10, 'F')
        doc.setTextColor(3, 7, 18)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(16)
        doc.text('ItaCondo', 32, 18)
      }
    } catch (err) {
      console.error("Error loading logo for PDF", err)
    }

    // Linha divisória
    doc.setDrawColor(220, 220, 220)
    doc.setLineWidth(0.5)
    doc.line(15, 28, pageWidth - 15, 28)

    // Cabeçalho Meta
    let yPos = 36
    doc.setTextColor(3, 7, 18)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(13)
    doc.text('RELATÓRIO DE HORAS TÉCNICAS', 15, yPos)

    yPos += 7
    doc.setFontSize(9)
    doc.setTextColor(80, 80, 80)
    
    const selectedProj = projetos.find(p => p.id === filterProjeto)
    const clientText = selectedProj 
      ? `Projeto: ${selectedProj.nome} | Cliente: ${selectedProj.clienteNome}` 
      : 'Filtro: Todos os Projetos'
    
    let periodText = 'Todos os períodos'
    if (filterMes) {
      const [year, month] = filterMes.split('-')
      const date = new Date(Number(year), Number(month) - 1, 1)
      const monthName = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
      periodText = monthName.charAt(0).toUpperCase() + monthName.slice(1)
    }

    doc.setFont('helvetica', 'normal')
    doc.text(clientText, 15, yPos)
    yPos += 5
    doc.text(`Período de Referência: ${periodText}`, 15, yPos)

    // Calcular faturamento dinâmico do relatório
    const pdfFaturamento = horasFiltradas.filter(h => h.tipo === "cobravel").reduce((sum, h) => {
      const proj = projetos.find(p => p.id === h.projetoId)
      const rate = proj?.valorHora ?? 130
      return sum + h.horasRegistradas * rate
    }, 0)

    // Card de Resumo com borda arredondada
    yPos += 8
    doc.setFillColor(240, 253, 244) // bg-emerald-50
    doc.setDrawColor(187, 247, 208) // border-emerald-200
    doc.roundedRect(15, yPos, pageWidth - 30, 20, 2, 2, 'FD')

    // Valores do Card
    doc.setTextColor(21, 128, 61)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.text('TOTAL DE HORAS REGISTRADAS', 22, yPos + 7)
    doc.setFontSize(14)
    doc.text(`${stats.total}h`, 22, yPos + 15)

    doc.setDrawColor(187, 247, 208)
    doc.line(pageWidth / 2, yPos + 3, pageWidth / 2, yPos + 17)

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.text('VALOR TOTAL FATURÁVEL (DINÂMICO)', pageWidth / 2 + 7, yPos + 7)
    doc.setFontSize(14)
    doc.text(fmtCurrency(pdfFaturamento), pageWidth / 2 + 7, yPos + 15)

    // Tabela detalhada
    yPos += 28
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.setTextColor(3, 7, 18)
    doc.text('Lançamentos Detalhados no Período', 15, yPos)

    const tableRows = horasFiltradas.map(h => {
      const proj = projetos.find(p => p.id === h.projetoId)
      const rate = proj?.valorHora ?? 130
      return [
        fmtDate(h.data),
        h.profissional,
        `${h.descricao}${h.tarefa ? `\n[Tarefa: ${h.tarefa}]` : ''}`,
        h.tipo === "cobravel" ? "Cobrável" : "Não cobrável",
        fmtHoras(h.horasRegistradas),
        h.tipo === "cobravel" ? fmtCurrency(h.horasRegistradas * rate) : "R$ 0,00"
      ]
    })

    autoTable(doc, {
      startY: yPos + 4,
      head: [['Data', 'Profissional', 'Atividade & Descrição', 'Tipo', 'Duração', 'Valor']],
      body: tableRows,
      theme: 'striped',
      headStyles: {
        fillColor: [3, 7, 18],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8.5
      },
      styles: {
        fontSize: 8,
        cellPadding: 4,
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 28 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 25 },
        4: { cellWidth: 16 },
        5: { cellWidth: 25 }
      },
      margin: { left: 15, right: 15 }
    })

    // Rodapé do PDF
    const footerY = pageHeight - 15
    doc.setDrawColor(230, 230, 230)
    doc.line(15, footerY - 4, pageWidth - 15, footerY - 4)
    
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7.5)
    doc.setTextColor(140, 140, 140)
    
    const now = new Date()
    const fmtNow = `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR')}`
    doc.text(`Relatório técnico gerado em: ${fmtNow} | ItaCondo Sistemas & Segurança`, 15, footerY)
    doc.text(`Página 1`, pageWidth - 25, footerY)

    // Salvar PDF
    const projectSlug = selectedProj 
      ? selectedProj.nome.replace(/\s+/g, '-').toLowerCase() 
      : 'geral'
    doc.save(`relatorio-horas-${projectSlug}-${filterMes || 'geral'}.pdf`)
    
    toast({
      title: "Sucesso",
      description: "Relatório em PDF exportado com sucesso."
    })
  }

  // Handlers horas
  const setHoraField = (k: keyof HoraTecnica, v: any) => setFormHora(p => ({ ...p, [k]: v }))
  const openNewHora = () => { setEditingHora(null); setFormHora(emptyHora()); setHoraOpen(true) }
  const openEditHora = (h: HoraTecnica) => { setEditingHora(h); setFormHora({ ...h }); setHoraOpen(true) }

  const handleSaveHora = () => {
    if (!formHora.profissional.trim() || !formHora.descricao.trim() || !formHora.projetoId) {
      toast({ title: "Erro", description: "Profissional, projeto e descrição são obrigatórios", variant: "destructive" })
      return
    }
    const projeto = projetos.find(p => p.id === formHora.projetoId)
    const updated: HoraTecnica = {
      ...formHora,
      projetoNome: projeto?.nome ?? formHora.projetoNome,
      updatedAt: new Date().toISOString(),
    }
    setHoras(prev => {
      const idx = prev.findIndex(h => h.id === updated.id)
      if (idx >= 0) { const arr = [...prev]; arr[idx] = updated; return arr }
      return [...prev, updated]
    })
    setHoraOpen(false)
    toast({ title: editingHora ? "Registro atualizado" : "Horas registradas", description: `${formHora.horasRegistradas}h — ${formHora.descricao}` })
  }

  const handleDeleteHora = (id: string) => {
    setHoras(prev => prev.filter(h => h.id !== id))
    toast({ title: "Registro excluído" })
  }

  const handleAprovar = (id: string) => {
    setHoras(prev => prev.map(h => h.id === id ? { ...h, aprovado: true } : h))
    toast({ title: "Horas aprovadas" })
  }

  // Handlers projetos
  const setProjetoField = (k: keyof Projeto, v: any) => setFormProjeto(p => ({ ...p, [k]: v }))
  const openNewProjeto = () => { setEditingProjeto(null); setFormProjeto(emptyProjeto()); setProjetoOpen(true) }
  const openEditProjeto = (p: Projeto) => { setEditingProjeto(p); setFormProjeto({ ...p }); setProjetoOpen(true) }

  const handleSaveProjeto = () => {
    if (!formProjeto.nome.trim()) { toast({ title: "Erro", description: "Nome do projeto é obrigatório", variant: "destructive" }); return }
    const updated = { ...formProjeto, updatedAt: new Date().toISOString() }
    setProjetos(prev => {
      const idx = prev.findIndex(p => p.id === updated.id)
      if (idx >= 0) { const arr = [...prev]; arr[idx] = updated; return arr }
      return [...prev, updated]
    })
    setProjetoOpen(false)
    toast({ title: editingProjeto ? "Projeto atualizado" : "Projeto criado" })
  }

  const handleDeleteProjeto = (id: string) => {
    setProjetos(prev => prev.filter(p => p.id !== id))
    toast({ title: "Projeto excluído" })
  }

  const rateOf = (projetoId: string) => projetos.find(p => p.id === projetoId)?.valorHora ?? 130
  const faturamentoFiltrado = horasFiltradas.filter(h => h.tipo === "cobravel").reduce((sum, h) => sum + h.horasRegistradas * rateOf(h.projetoId), 0)
  const PROJETO_STATUS: Record<string, { label: string; tone: Tone }> = {
    ativo: { label: "Ativo", tone: "success" },
    pausado: { label: "Pausado", tone: "warning" },
    concluido: { label: "Concluído", tone: "accent" },
    cancelado: { label: "Cancelado", tone: "neutral" },
  }
  const tipoTag = (tipo: TipoHora) => tipo === "cobravel" ? <Tag tone="success">Cobrável</Tag> : <Tag>Não cobrável</Tag>

  return (
    <SuperAdminLayout>
      <Page>
        <PageHeader
          title="Horas técnicas"
          description="Horas de instalação e suporte por projeto, com faturamento estimado e relatório em PDF."
          actions={
            <>
              <Button variant="outline" className="h-9 gap-1.5" onClick={openNewProjeto}><Briefcase className="h-4 w-4" />Novo projeto</Button>
              <Button className="h-9 gap-1.5" onClick={openNewHora}><Plus className="h-4 w-4" />Registrar horas</Button>
            </>
          }
        />

        <Segmented
          value={tab}
          onChange={setTab}
          className="border-b border-border pb-3"
          options={[
            { value: "dashboard", label: "Resumo" },
            { value: "horas", label: "Lançamentos", count: horas.length },
            { value: "projetos", label: "Projetos", count: projetos.length },
            { value: "relatorio", label: "Relatório" },
          ]}
        />

        {tab === "dashboard" && (
          <>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Select value={dashboardPeriodo} onValueChange={setDashboardPeriodo}>
                <SelectTrigger className="h-8 w-full text-[13px] sm:w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="semana">Últimos 7 dias</SelectItem>
                  <SelectItem value="mes">Este mês</SelectItem>
                  <SelectItem value="mes_anterior">Mês anterior</SelectItem>
                  <SelectItem value="personalizado">Período personalizado</SelectItem>
                </SelectContent>
              </Select>
              {dashboardPeriodo === "personalizado" && (
                <>
                  <Input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} className="h-8 w-full text-[13px] [color-scheme:dark] sm:w-40" aria-label="Início" />
                  <Input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} className="h-8 w-full text-[13px] [color-scheme:dark] sm:w-40" aria-label="Fim" />
                </>
              )}
              <Select value={dashboardProjetoId} onValueChange={setDashboardProjetoId}>
                <SelectTrigger className="h-8 w-full text-[13px] sm:w-56"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os projetos</SelectItem>
                  {projetos.map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <StatGrid>
              <StatCard label="Horas no período" value={`${dashboardStats.total}h`} icon={Clock} tone="accent" />
              <StatCard label="Faturamento estimado" value={fmtCurrency(dashboardStats.faturavelValue)} hint="Horas cobráveis × valor/hora" icon={DollarSign} tone="success" />
              <StatCard label="Atividades aprovadas" value={dashboardStats.concluidas} icon={CheckCircle2} />
              <StatCard label="Projetos atendidos" value={dashboardStats.clientesAtendidos} icon={Briefcase} />
            </StatGrid>

            <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1fr)_340px]">
              <Panel title="Horas por dia" description="Cobráveis e não cobráveis" bodyClassName="px-2 pb-2 pt-4">
                {chartData.length === 0 ? (
                  <div className="grid h-[260px] place-items-center text-[13px] text-muted-foreground">Nenhum lançamento no período.</div>
                ) : (
                  <div className="h-[260px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke="hsl(228 8% 14%)" />
                        <XAxis dataKey="data" axisLine={false} tickLine={false} tick={{ fill: "hsl(225 6% 50%)", fontSize: 11 }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: "hsl(225 6% 50%)", fontSize: 11 }} />
                        <Tooltip
                          cursor={{ fill: "hsl(228 8% 12%)" }}
                          contentStyle={{ background: "hsl(228 9% 9%)", border: "1px solid hsl(228 8% 17%)", borderRadius: 8, color: "hsl(225 10% 93%)", fontSize: 12 }}
                          labelStyle={{ color: "hsl(225 6% 60%)" }}
                        />
                        <Bar dataKey="cobravel" name="Cobrável" fill="hsl(224 100% 64%)" radius={[0, 0, 0, 0]} stackId="a" />
                        <Bar dataKey="naoCobravel" name="Não cobrável" fill="hsl(228 8% 26%)" radius={[3, 3, 0, 0]} stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Panel>

              <Panel title="Faturamento por projeto" flush>
                {dashboardProjetosResumo.length === 0 ? (
                  <div className="px-4 py-10 text-center text-[13px] text-muted-foreground">Nada faturável no período.</div>
                ) : (
                  <ul className="divide-y divide-border">
                    {dashboardProjetosResumo.map(p => (
                      <li key={p.id} className="px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-[13px] font-medium text-foreground">{p.nome}</div>
                            <div className="truncate text-[12px] text-muted-foreground">{p.cliente}</div>
                          </div>
                          <span className="text-[13px] font-semibold tabular-nums text-foreground">{fmtCurrency(p.faturavel)}</span>
                        </div>
                        <div className="mt-1 text-[11px] tabular-nums text-muted-foreground">{p.total}h no total · {p.cobravel}h cobráveis</div>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>
          </>
        )}

        {tab === "horas" && (
          <Panel flush>
            <Toolbar>
              <SearchInput value={search} onChange={setSearch} placeholder="Projeto, profissional ou descrição" />
              <div className="flex flex-col gap-2 sm:flex-row lg:ml-auto">
                <Select value={filterProjeto} onValueChange={setFilterProjeto}>
                  <SelectTrigger className="h-8 w-full text-[13px] sm:w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os projetos</SelectItem>
                    {projetos.map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterTipo} onValueChange={setFilterTipo}>
                  <SelectTrigger className="h-8 w-full text-[13px] sm:w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    <SelectItem value="cobravel">Cobrável</SelectItem>
                    <SelectItem value="nao_cobravel">Não cobrável</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="month" value={filterMes} onChange={e => setFilterMes(e.target.value)} className="h-8 w-full text-[13px] [color-scheme:dark] sm:w-40" aria-label="Mês" />
              </div>
            </Toolbar>
            {horasFiltradas.length === 0 ? (
              <EmptyState icon={Clock} title="Nenhum lançamento encontrado" description={projetos.length ? "Ajuste os filtros ou registre horas." : "Crie um projeto e registre as primeiras horas."} />
            ) : (
              <>
                <DataTable minWidth={900}>
                  <thead>
                    <tr className="border-b border-border">
                      <th className={th}>Data</th>
                      <th className={th}>Atividade</th>
                      <th className={th}>Profissional</th>
                      <th className={cn(th, "text-right")}>Horas</th>
                      <th className={th}>Tipo</th>
                      <th className={th}>Aprovação</th>
                      <th className={cn(th, "w-px")}><span className="sr-only">Ações</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {horasPager.slice.map(h => (
                      <tr key={h.id} className={rowClass} onClick={() => openEditHora(h)}>
                        <td className={cn(td, "text-[13px] tabular-nums text-muted-foreground")}>{fmtDate(h.data)}</td>
                        <td className={td}>
                          <div className="max-w-[280px] truncate text-[13px] text-foreground">{h.descricao}</div>
                          <div className="max-w-[280px] truncate text-[12px] text-muted-foreground">{h.projetoNome}{h.tarefa ? ` · ${h.tarefa}` : ""}</div>
                        </td>
                        <td className={cn(td, "text-[13px] text-foreground")}>{h.profissional}</td>
                        <td className={cn(td, "text-right text-[13px] font-medium tabular-nums text-foreground")}>{fmtHoras(h.horasRegistradas)}</td>
                        <td className={td}>{tipoTag(h.tipo)}</td>
                        <td className={td}>{h.aprovado ? <Tag tone="success" dot>Aprovada</Tag> : <Tag tone="warning" dot>Pendente</Tag>}</td>
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              {!h.aprovado && <DropdownMenuItem onSelect={() => handleAprovar(h.id)}><CheckCircle2 className="mr-2 h-4 w-4" />Aprovar</DropdownMenuItem>}
                              <DropdownMenuItem onSelect={() => openEditHora(h)}><Pencil className="mr-2 h-4 w-4" />Editar</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onSelect={() => setDeleting({ kind: "hora", id: h.id })} className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"><Trash2 className="mr-2 h-4 w-4" />Excluir</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
                <Pagination {...horasPager} noun="lançamentos" />
              </>
            )}
          </Panel>
        )}

        {tab === "projetos" && (
          <Panel flush>
            {projetos.length === 0 ? (
              <EmptyState icon={Briefcase} title="Nenhum projeto cadastrado" description="Projetos agrupam as horas e definem o valor/hora." action={<Button size="sm" className="gap-1.5" onClick={openNewProjeto}><Plus className="h-4 w-4" />Novo projeto</Button>} />
            ) : (
              <DataTable minWidth={820}>
                <thead>
                  <tr className="border-b border-border">
                    <th className={th}>Projeto</th>
                    <th className={th}>Consumo</th>
                    <th className={cn(th, "text-right")}>Valor/hora</th>
                    <th className={th}>Status</th>
                    <th className={cn(th, "w-px")}><span className="sr-only">Ações</span></th>
                  </tr>
                </thead>
                <tbody>
                  {projetos.map(p => {
                    const usadas = horas.filter(h => h.projetoId === p.id).reduce((s, h) => s + h.horasRegistradas, 0)
                    const previstas = p.horasPrevistas ?? 0
                    const pct = previstas ? Math.min(100, (usadas / previstas) * 100) : 0
                    const estourou = previstas > 0 && usadas > previstas
                    const st = PROJETO_STATUS[p.status] ?? PROJETO_STATUS.cancelado
                    return (
                      <tr key={p.id} className={rowClass} onClick={() => openEditProjeto(p)}>
                        <td className={td}>
                          <div className="max-w-[280px] truncate text-[13px] font-medium text-foreground">{p.nome}</div>
                          <div className="max-w-[280px] truncate text-[12px] text-muted-foreground">{p.clienteNome}</div>
                        </td>
                        <td className={cn(td, "w-64")}>
                          <div className="flex justify-between text-[12px] tabular-nums">
                            <span className={estourou ? "text-[hsl(358_90%_72%)]" : "text-foreground"}>{fmtHoras(usadas)}</span>
                            <span className="text-muted-foreground">{previstas ? `de ${previstas}h` : "sem previsão"}</span>
                          </div>
                          {previstas > 0 && (
                            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-[hsl(228_8%_14%)]">
                              <div className={cn("h-full rounded-full", estourou ? "bg-[hsl(358_82%_62%)]" : "bg-[hsl(224_100%_64%)]")} style={{ width: `${pct}%` }} />
                            </div>
                          )}
                        </td>
                        <td className={cn(td, "text-right text-[13px] tabular-nums text-foreground")}>{fmtCurrency(p.valorHora ?? 130)}</td>
                        <td className={td}><Tag tone={st.tone} dot>{st.label}</Tag></td>
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem onSelect={() => openEditProjeto(p)}><Pencil className="mr-2 h-4 w-4" />Editar</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onSelect={() => setDeleting({ kind: "projeto", id: p.id })} className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"><Trash2 className="mr-2 h-4 w-4" />Excluir</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </DataTable>
            )}
          </Panel>
        )}

        {tab === "relatorio" && (
          <Panel
            title="Relatório de horas"
            description="Dados consolidados e lista de atividades, exportáveis em PDF."
            actions={<Button size="sm" className="h-8 gap-1.5" onClick={handleDownloadPDF}><Download className="h-4 w-4" />Baixar PDF</Button>}
            flush
          >
            <Toolbar>
              <Select value={filterProjeto} onValueChange={setFilterProjeto}>
                <SelectTrigger className="h-8 w-full text-[13px] sm:w-56"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os projetos</SelectItem>
                  {projetos.map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="month" value={filterMes} onChange={e => setFilterMes(e.target.value)} className="h-8 w-full text-[13px] [color-scheme:dark] sm:w-40" aria-label="Mês" />
              <div className="flex gap-5 text-[12px] text-muted-foreground lg:ml-auto">
                <span>Total <strong className="tabular-nums text-foreground">{stats.total}h</strong></span>
                <span>Cobráveis <strong className="tabular-nums text-foreground">{stats.cobravel}h</strong></span>
                <span>Estimado <strong className="tabular-nums text-foreground">{fmtCurrency(faturamentoFiltrado)}</strong></span>
              </div>
            </Toolbar>
            {horasFiltradas.length === 0 ? (
              <EmptyState icon={FileText} title="Nada no período" description="Ajuste o projeto ou o mês." />
            ) : (
              <DataTable minWidth={860}>
                <thead>
                  <tr className="border-b border-border">
                    <th className={th}>Data</th>
                    <th className={th}>Atividade</th>
                    <th className={th}>Profissional</th>
                    <th className={cn(th, "text-right")}>Duração</th>
                    <th className={th}>Tipo</th>
                    <th className={cn(th, "text-right")}>Valor estimado</th>
                  </tr>
                </thead>
                <tbody>
                  {horasFiltradas.map(h => (
                    <tr key={h.id} className="border-t border-border first:border-t-0">
                      <td className={cn(td, "text-[13px] tabular-nums text-muted-foreground")}>{fmtDate(h.data)}</td>
                      <td className={td}>
                        <div className="max-w-[300px] truncate text-[13px] text-foreground">{h.descricao}</div>
                        <div className="max-w-[300px] truncate text-[12px] text-muted-foreground">{h.projetoNome}{h.tarefa ? ` · ${h.tarefa}` : ""}</div>
                      </td>
                      <td className={cn(td, "text-[13px] text-foreground")}>{h.profissional}</td>
                      <td className={cn(td, "text-right text-[13px] tabular-nums text-foreground")}>{fmtHoras(h.horasRegistradas)}</td>
                      <td className={td}>{tipoTag(h.tipo)}</td>
                      <td className={cn(td, "text-right text-[13px] tabular-nums text-foreground")}>{h.tipo === "cobravel" ? fmtCurrency(h.horasRegistradas * rateOf(h.projetoId)) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </Panel>
        )}
      </Page>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{deleting?.kind === "projeto" ? "Excluir projeto?" : "Excluir lançamento?"}</AlertDialogTitle>
            <AlertDialogDescription>{deleting?.kind === "projeto" ? "O projeto deixa de aparecer nos filtros e relatórios." : "Este registro de horas será removido."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90"
              onClick={() => { if (!deleting) return; if (deleting.kind === "projeto") handleDeleteProjeto(deleting.id); else handleDeleteHora(deleting.id) }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Editor de Horas */}
      <Dialog open={horaOpen} onOpenChange={setHoraOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingHora ? "Editar Lançamento" : "Registrar Horas"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data *</Label>
                <Input type="date" value={formHora.data} onChange={e => setHoraField("data", e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Horas *</Label>
                <Input type="number" min="0.01" step="0.01" value={formHora.horasRegistradas} onChange={e => setHoraField("horasRegistradas", Number(e.target.value))} className="mt-1" />
              </div>
            </div>

            {/* Stopwatch Tracker */}
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Timer className={`h-5 w-5 text-muted-foreground ${timerActive ? "animate-pulse" : ""}`} />
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Cronômetro de Trabalho</p>
                  <p className="text-sm font-mono font-bold text-slate-800">{formatStopwatchTime(secondsElapsed)}</p>
                </div>
              </div>
              <div className="flex gap-1.5">
                {!timerActive ? (
                  <Button type="button" size="sm" onClick={handleStartTimer} className="bg-green-600 hover:bg-green-700 text-white font-semibold text-xs px-2.5 h-8">
                    Iniciar
                  </Button>
                ) : (
                  <Button type="button" size="sm" onClick={handlePauseTimer} variant="outline" className="text-xs px-2.5 h-8">
                    Pausar
                  </Button>
                )}
                <Button type="button" size="sm" onClick={handleStopTimer} disabled={secondsElapsed === 0} className="bg-red-600 hover:bg-red-700 text-white font-semibold text-xs px-2.5 h-8">
                  Parar
                </Button>
              </div>
            </div>
            <div>
              <Label>Profissional *</Label>
              <Select value={formHora.profissional} onValueChange={v => setHoraField("profissional", v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione ou digite" /></SelectTrigger>
                <SelectContent>
                  {PROFISSIONAIS_DEFAULT.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Projeto *</Label>
              {projetos.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-1">⚠ Crie um projeto primeiro na aba "Projetos"</p>
              ) : (
                <Select value={formHora.projetoId} onValueChange={v => setHoraField("projetoId", v)}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione o projeto" /></SelectTrigger>
                  <SelectContent>
                    {projetos.map(p => <SelectItem key={p.id} value={p.id}>{p.nome} — {p.clienteNome}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Tipo de Hora</Label>
              <Select value={formHora.tipo} onValueChange={v => setHoraField("tipo", v as TipoHora)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cobravel">✅ Cobrável</SelectItem>
                  <SelectItem value="nao_cobravel">⭕ Não cobrável</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descrição *</Label>
              <Textarea value={formHora.descricao} onChange={e => setHoraField("descricao", e.target.value)} placeholder="O que foi feito neste período?" className="mt-1 min-h-[70px]" />
            </div>
            <div>
              <Label>Tarefa / Card (opcional)</Label>
              <Input value={formHora.tarefa ?? ""} onChange={e => setHoraField("tarefa", e.target.value)} placeholder="Ex: Configuração do módulo de portaria" className="mt-1" />
            </div>
          </div>
          <DialogFooter className="gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setHoraOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveHora} className="gap-2">
              <Clock className="h-4 w-4" />
              {editingHora ? "Atualizar" : "Registrar Horas"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor de Projeto */}
      <Dialog open={projetoOpen} onOpenChange={setProjetoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingProjeto ? "Editar Projeto" : "Novo Projeto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Projeto *</Label>
              <Input value={formProjeto.nome} onChange={e => setProjetoField("nome", e.target.value)} placeholder="Ex: Implantação — Condomínio Solar" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cliente *</Label>
                <Input value={formProjeto.clienteNome} onChange={e => setProjetoField("clienteNome", e.target.value)} placeholder="Nome do cliente" className="mt-1" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formProjeto.status} onValueChange={v => setProjetoField("status", v as any)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="pausado">Pausado</SelectItem>
                    <SelectItem value="concluido">Concluído</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>E-mail do Cliente</Label>
              <Input type="email" value={formProjeto.clienteEmail ?? ""} onChange={e => setProjetoField("clienteEmail", e.target.value)} placeholder="email@exemplo.com" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Horas Previstas</Label>
                <Input type="number" min="0" step="1" value={formProjeto.horasPrevistas ?? 0} onChange={e => setProjetoField("horasPrevistas", Number(e.target.value))} className="mt-1" />
              </div>
              <div>
                <Label>Valor da Hora (R$)</Label>
                <Input type="number" min="0" step="5" value={formProjeto.valorHora ?? 130} onChange={e => setProjetoField("valorHora", Number(e.target.value))} className="mt-1" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setProjetoOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveProjeto} className="">Salvar Projeto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  )
}

export default HorasTecnicas
